import { Button } from '@/components/ui/button'
import { ForumBody } from '@/components/forum/forum-body'
import {
  ForumAdminBadge,
  ForumPinnedBadge,
} from '@/components/forum/forum-badges'
import {
  localizedBoardName,
  pickTranslation,
} from '@/components/forum/forum-i18n'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { formatDate } from '@/lib/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

type Reply = {
  id: string
  body: string
  parentId: string | null
  createdAt: Date | string
  authorName: string
  isAdminAuthor: boolean
}

type FlatReply = Reply & { depth: number }

const MAX_INDENT = 4
const INDENT_PX = 20

function flattenTree(replies: Reply[]): FlatReply[] {
  const childrenByParent = new Map<string | null, Reply[]>()
  for (const reply of replies) {
    const key = reply.parentId
    const list = childrenByParent.get(key) ?? []
    list.push(reply)
    childrenByParent.set(key, list)
  }

  const out: FlatReply[] = []
  const walk = (parentId: string | null, depth: number) => {
    for (const reply of childrenByParent.get(parentId) ?? []) {
      out.push({ ...reply, depth })
      walk(reply.id, depth + 1)
    }
  }
  walk(null, 0)
  return out
}

export function ForumThreadPage() {
  const { threadId = '' } = useParams()
  const { t, language } = useI18n()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()

  const { data, isLoading } = useQuery(
    trpc.forumRouter.getThread.queryOptions({ threadId }),
  )
  const replyData = data?.replies as Reply[] | undefined
  const flat = useMemo(() => flattenTree(replyData ?? []), [replyData])

  const [activeReplyTarget, setActiveReplyTarget] = useState<string | null>(
    null,
  )
  const [draft, setDraft] = useState('')
  const [highlightedReplyId, setHighlightedReplyId] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (!location.hash.startsWith('#reply-') || flat.length === 0) {
      return
    }

    const replyId = location.hash.slice('#reply-'.length)
    const element = document.getElementById(`reply-${replyId}`)
    if (!element) {
      return
    }

    setHighlightedReplyId(replyId)
    const scrollTimer = window.setTimeout(() => {
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 0)
    const highlightTimer = window.setTimeout(() => {
      setHighlightedReplyId(current =>
        current === replyId ? null : current,
      )
    }, 4000)

    return () => {
      window.clearTimeout(scrollTimer)
      window.clearTimeout(highlightTimer)
    }
  }, [flat, location.hash])

  const createReply = useMutation({
    ...trpc.forumRouter.createReply.mutationOptions(),
    onSuccess: () => {
      setDraft('')
      setActiveReplyTarget(null)
      queryClient.invalidateQueries({
        queryKey: trpc.forumRouter.getThread.queryOptions({ threadId })
          .queryKey,
      })
    },
    onError: () => toast.error(t('forum.errorGeneric')),
  })

  const submitReply = (parentId: string | null) => {
    const body = draft.trim()
    if (!body) {
      toast.error(t('forum.bodyRequired'))
      return
    }
    createReply.mutate({ threadId, body, ...(parentId ? { parentId } : {}) })
  }

  const openReplyBox = (target: string | null) => {
    setActiveReplyTarget(target)
    setDraft('')
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 pb-12 pt-24 text-center text-muted-foreground md:px-6">
        {t('forum.loading')}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 pb-12 pt-24 md:px-6">
        <p className="text-muted-foreground">{t('forum.threadNotFound')}</p>
        <Link
          to="/forum"
          className="mt-4 inline-block text-sm text-primary hover:underline"
        >
          {t('forum.backToForum')}
        </Link>
      </div>
    )
  }

  const { thread } = data
  const threadReplies = data.replies
  const localized = pickTranslation(thread.translations, language)
  const threadTitle = localized?.title ?? thread.title
  const threadBody = localized?.body ?? thread.body

  const renderReplyForm = (parentId: string | null) => (
    <div className="mt-3 space-y-2">
      <label htmlFor={`reply-${parentId ?? 'root'}`} className="sr-only">
        {t('forum.replyPlaceholder')}
      </label>
      <Textarea
        id={`reply-${parentId ?? 'root'}`}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder={t('forum.replyPlaceholder')}
        rows={3}
        autoFocus={parentId !== null}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={createReply.isPending}
          onClick={() => submitReply(parentId)}
        >
          {createReply.isPending ? t('forum.posting') : t('forum.postReply')}
        </Button>
        {parentId !== null && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setActiveReplyTarget(null)}
          >
            {t('forum.cancel')}
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-16 pt-24 md:px-6">
      <Link
        to={`/forum?board=${thread.board.slug}`}
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {localizedBoardName(t, thread.board)}
      </Link>

      { }
      <article className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="min-w-0 text-2xl font-bold text-foreground">
            {threadTitle}
          </h1>
          {thread.isPinned && <ForumPinnedBadge />}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <span>
            {t('forum.by')} {thread.authorName}
          </span>
          {thread.isAdminAuthor && <ForumAdminBadge />}
          <span>
            {' · '}
            {formatDate(thread.createdAt)}
          </span>
        </div>
        <ForumBody body={threadBody} className="mt-4" />
      </article>

      { }
      <section className="mt-6">
        {isAuthenticated ? (
          activeReplyTarget === null ? (
            renderReplyForm(null)
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openReplyBox(null)}
            >
              {t('forum.reply')}
            </Button>
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/signin?redirect=${encodeURIComponent(`/forum/thread/${threadId}`)}`,
                )}
              className="text-primary hover:underline"
            >
              {t('forum.signIn')}
            </button>{' '}
            {t('forum.signInToPost')}
          </p>
        )}
      </section>

      { }
      <section className="mt-6">
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">
          {t('forum.replies', { count: threadReplies.length })}
        </h2>
        <div className="space-y-3">
          {flat.map(reply => (
            <div
              key={reply.id}
              id={`reply-${reply.id}`}
              style={{
                marginLeft: Math.min(reply.depth, MAX_INDENT) * INDENT_PX,
              }}
              className={`scroll-mt-24 ${
                reply.depth > 0 ? 'border-l border-border pl-3' : ''
              }`}
            >
              <div
                className={`rounded-md border bg-card p-3 transition-shadow ${
                  highlightedReplyId === reply.id
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-border'
                }`}
              >
                <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  <span>{reply.authorName}</span>
                  {reply.isAdminAuthor && <ForumAdminBadge />}
                  <span>
                    {' · '}
                    {formatDate(reply.createdAt)}
                  </span>
                </div>
                <ForumBody body={reply.body} compact className="mt-1" />
                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => openReplyBox(reply.id)}
                    className="mt-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {t('forum.reply')}
                  </button>
                )}
                {activeReplyTarget === reply.id && renderReplyForm(reply.id)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
